import { FC, useState, useEffect } from "react";

import { Button, Checkbox, Divider, Flex, Typography } from "antd";
import { FilterDropdownProps } from "antd/es/table/interface";

interface CheckboxesFilterProps {
  dropdownProps: FilterDropdownProps;
  options?: string[];
}

type CheckboxState = 'none' | 'include' | 'exclude';

const defaultCheckboxOptions = [
  "Vide (Sans valeur)",
  "Prix non représentatif", 
  "Prix ZD",
  "Attique",
  "Maison individuelle",
  "Maison mitoyenne", 
  "QPT",
  "Pieds dans l'eau",
  "Vue lac",
  "Piscine",
  "Combles",
  "Promotion"
];

const CheckboxesFilter: FC<CheckboxesFilterProps> = ({ dropdownProps, options }) => {
  const checkboxOptions = options || defaultCheckboxOptions;
  const [checkboxStates, setCheckboxStates] = useState<Record<string, CheckboxState>>({});
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize from selectedKeys only once
  useEffect(() => {
    if (!isInitialized) {
      const keys = dropdownProps.selectedKeys as string[];
      const newStates: Record<string, CheckboxState> = {};
      
      // Initialize all checkboxes to 'none'
      checkboxOptions.forEach(option => {
        newStates[option] = 'none';
      });
      
      // Set states based on selectedKeys
      keys.forEach(key => {
        if (key.startsWith("NOT:")) {
          const actualValue = key.substring(4);
          newStates[actualValue] = 'exclude';
        } else {
          newStates[key] = 'include';
        }
      });
      
      setCheckboxStates(newStates);
      setIsInitialized(true);
    }
  }, [dropdownProps.selectedKeys, isInitialized]);

  const handleCheckboxClick = (option: string) => {
    const currentState = checkboxStates[option] || 'none';
    let newState: CheckboxState;
    
    // Cycle through states: none -> include -> exclude -> none
    switch (currentState) {
      case 'none':
        newState = 'include';
        break;
      case 'include':
        newState = 'exclude';
        break;
      case 'exclude':
        newState = 'none';
        break;
      default:
        newState = 'none';
    }
    
    const newStates = {
      ...checkboxStates,
      [option]: newState
    };
    
    setCheckboxStates(newStates);
    updateSelectedKeys(newStates);
  };

  const updateSelectedKeys = (states: Record<string, CheckboxState>) => {
    const selectedKeys: string[] = [];
    
    Object.entries(states).forEach(([option, state]) => {
      if (state === 'include') {
        selectedKeys.push(option);
      } else if (state === 'exclude') {
        selectedKeys.push(`NOT:${option}`);
      }
    });
    
    dropdownProps.setSelectedKeys(selectedKeys);
  };

  const getCheckboxProps = (option: string) => {
    const state = checkboxStates[option] || 'none';
    
    switch (state) {
      case 'include':
        return { checked: true, indeterminate: false };
      case 'exclude':
        return { checked: false, indeterminate: true };
      case 'none':
      default:
        return { checked: false, indeterminate: false };
    }
  };

  const getCheckboxLabel = (option: string) => {
    const state = checkboxStates[option] || 'none';
    const baseLabel = option;
    
    switch (state) {
      case 'include':
        return baseLabel;
      case 'exclude':
        return `${baseLabel} (exclu)`;
      case 'none':
      default:
        return baseLabel;
    }
  };

  return (
    <Flex vertical gap={8} style={{ padding: 8, width: 280 }}>
      <Flex vertical style={{ paddingLeft: 16, paddingRight: 16 }}>
        <Typography.Text type="secondary" style={{ fontSize: 12, marginBottom: 8 }}>
          Cliquez plusieurs fois pour changer l'état (ignoré, inclus, exclu)
        </Typography.Text>
        
        {checkboxOptions.map(option => (
          <Checkbox
            key={option}
            style={{ width: "100%", marginBottom: 4 }}
            {...getCheckboxProps(option)}
            onClick={() => handleCheckboxClick(option)}
          >
            {getCheckboxLabel(option)}
          </Checkbox>
        ))}
      </Flex>
      <Divider style={{ marginTop: 0, marginBottom: 0 }} />
      <Flex justify="space-between">
        <Button
          onClick={() => {
            const resetStates: Record<string, CheckboxState> = {};
            checkboxOptions.forEach(option => {
              resetStates[option] = 'none';
            });
            setCheckboxStates(resetStates);
            setIsInitialized(true);
            dropdownProps.setSelectedKeys([]);
          }}
          type="link"
          size="small"
          disabled={dropdownProps.selectedKeys.length === 0}
        >
          Réinitialiser
        </Button>
        <Button
          onClick={() => dropdownProps.confirm()}
          type="primary"
          size="small"
        >
          OK
        </Button>
      </Flex>
    </Flex>
  );
};

export default CheckboxesFilter;
